import { useQueries, useQuery } from '@tanstack/react-query';

import { BUSINESS_DASHBOARD_MAX_PAGES } from '~/consts/businessTraveller';

import { getBusinessList, getEmployeeList } from '~/services/BusinessTraveller/BusinessTravellerService';
import { request } from '~/services/common';

import { createConcurrencyLimiter } from '~/utils/createConcurrencyLimiter';

export interface DashboardEmployee {
  id: string;
  customer_id: string;
  credit_customer_id: string;
  tier: App.LuxLoyaltyTier | null;
  societe_enrolment?: 'enrolled' | 'not-enrolled' | 'unknown';
  qualified_orders?: number | null;
  qualification_start?: string | null;
  ttv: number;
  margin: number;
  orders: number;
}

export interface DashboardBusiness {
  id: string;
  name: string;
  ttv: number | null;
  margin: number | null;
  orders: number | null;
  snapshotAvailable?: boolean;
  last_booking_at?: string | null;
  source_updated_at: string | null;
  employees: Array<DashboardEmployee>;
}

export function useBusinessDashboard(enabled: boolean) {
  return useQuery<{ generatedAt: string; overviewVersion?: number; businesses: Array<DashboardBusiness> }>({
    queryKey: ['business-traveller', 'lifetime-dashboard'],
    queryFn: async ({ signal }) => {
      const snapshot: { generatedAt: string; overviewVersion?: number; businesses: Array<DashboardBusiness> } =
        await request(`${window.configs.API_HOST}/api/reporting/business-traveller/business-performance`, { signal });
      const businesses = new Map(
        snapshot.businesses.map((business) => [business.id, { ...business, snapshotAvailable: true }]),
      );
      for (let page = 0; page < BUSINESS_DASHBOARD_MAX_PAGES; page++) {
        if (signal.aborted) throw signal.reason;
        const response = await getBusinessList(100, page);
        for (const business of response.result) {
          const existing = businesses.get(business.id);
          businesses.set(
            business.id,
            existing
              ? { ...existing, name: business.name }
              : {
                  id: business.id,
                  name: business.name,
                  ttv: null,
                  margin: null,
                  orders: null,
                  source_updated_at: '',
                  employees: [],
                  snapshotAvailable: false,
                },
          );
        }
        if (
          !response.result.length ||
          (response.meta ? (page + 1) * 100 >= response.meta.total : response.result.length < 100)
        )
          return { ...snapshot, businesses: Array.from(businesses.values()) };
      }
      throw new Error('Business list exceeded the dashboard page limit.');
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

const loadEmployees = createConcurrencyLimiter(2);

function dashboardEmployeesQueryOptions(businessId: string) {
  return {
    queryKey: ['business-traveller', 'dashboard-employees', businessId],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      loadEmployees(async () => {
        const employees: Array<App.BusinessEmployee> = [];
        for (let page = 0; page < BUSINESS_DASHBOARD_MAX_PAGES; page++) {
          if (signal.aborted) throw signal.reason;
          const response = await getEmployeeList(businessId, { page, pageSize: 100 });
          employees.push(...response.result);
          if (!response.result.length || employees.length >= (response.meta?.total ?? Infinity)) {
            return employees;
          }
        }
        throw new Error('Employee list exceeded the dashboard page limit.');
      }, signal),
    staleTime: 60_000,
  };
}

export function useDashboardEmployees(businessId: string, enabled: boolean) {
  return useQuery({ ...dashboardEmployeesQueryOptions(businessId), enabled });
}

export function useDashboardCustomerSearch(
  businessIds: Array<string>,
  search: string,
  suggest: boolean,
  selectedEmployeeId: string | null,
) {
  const term = search.trim().toLowerCase();
  const active = term.length >= 2 || !!selectedEmployeeId;
  const queries = useQueries({
    queries: businessIds.map((id) => ({ ...dashboardEmployeesQueryOptions(id), enabled: active || suggest })),
  });
  const matches = new Map(
    businessIds.map((id, index) => [
      id,
      new Set(
        (queries[index].data ?? [])
          .filter((employee) =>
            selectedEmployeeId
              ? employee.id === selectedEmployeeId
              : `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(term) ||
                (employee.email ?? '').toLowerCase().includes(term),
          )
          .map((employee) => employee.id),
      ),
    ]),
  );
  return {
    active,
    matches,
    suggestions: businessIds.flatMap((businessId, index) =>
      (queries[index].data ?? []).map((employee) => ({ ...employee, businessId })),
    ),
    suggestionsLoading: (active || suggest) && queries.some((query) => query.isPending),
    isLoading: active && queries.some((query) => query.isPending),
    isError: active && queries.some((query) => query.isError),
  };
}
